import DOMPurify from 'dompurify'
import hljs from 'highlight.js'
import { marked } from 'marked'
import { markedHighlight } from 'marked-highlight'
import { FlagBadge, RingBadge } from '@/components/Badge/Badge'
import { Tag } from '@/components/Tags/Tags'
import { getReleaseDates } from '@/lib/data'
import { useRadarNav } from '@/lib/state'
import { cn } from '@/lib/utils'
import type { Flag, Item, ItemRelease } from '../../../../types'
import styles from './ItemDetail.module.css'

// Add CSS for proper heading spacing in markdown content
const markdownStyles = {
  prose: `prose prose-sm max-w-none
    prose-headings:font-semibold
    prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-xl
    prose-h3:mt-6 prose-h3:mb-3 prose-h3:text-lg
    prose-p:my-3
    prose-ul:my-3
    prose-li:my-1
  `
}

// Configure marked with syntax highlighting
marked.use(
  markedHighlight({
    langPrefix: 'hljs language-',
    highlight(code, lang) {
      const language = hljs.getLanguage(lang) ? lang : 'plaintext'
      return hljs.highlight(code, { language }).value
    }
  })
)

// Set marked options for secure defaults
marked.setOptions({
  gfm: true, // GitHub Flavored Markdown
  breaks: true // Convert line breaks to <br>
})

// Helper to parse ISO date as local date
function parseLocalDate(isoDateStr: string) {
  const [year, month, day] = isoDateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

interface ItemProps {
  item: Item
}

export function ItemDetail({ item }: ItemProps) {
  const { route } = useRadarNav()
  const releases = getReleaseDates().slice(-3)
  const currentRelease = item.releases[0] // Most recent release

  const _isNotMaintained = (releaseDate: string) => {
    return !releases.includes(releaseDate)
  }

  return (
    <div data-testid="item-detail">
      <div data-testid="item-detail-header" className={styles.header}>
        <div className="flex justify-between items-center w-full">
          <h1 className={styles.title}>
            {currentRelease.title}
            {currentRelease.flag && (
              <span style={{ marginLeft: 8, display: 'inline-block' }}>
                <FlagBadge flag={currentRelease.flag as Flag} />
              </span>
            )}
          </h1>
          <div
            data-testid="item-detail-actions"
            className="flex items-center gap-2"
          >
            {item.tags?.map(tag => (
              <Tag
                key={tag}
                tag={tag}
                onClick={() => route(`overview?tag=${tag}`)}
              />
            ))}
          </div>
        </div>
      </div>
      <div data-testid="item-detail-content" className={styles.revisions}>
        <Revision id={item.id} release={currentRelease} />
        {item.releases.slice(1).map(release => (
          <Revision
            key={`${item.id}-${release.releaseDate}`}
            id={item.id}
            release={release}
          />
        ))}
      </div>
    </div>
  )
}

interface RevisionProps {
  id: string
  release: ItemRelease
}

function Revision({ release }: RevisionProps) {
  const date = parseLocalDate(release.releaseDate)
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  const year = date.getFullYear()
  const formattedDate = `${month} ${year}`

  const renderMarkdown = (markdown: string) => {
    // Convert markdown to HTML and sanitize
    const rawHtml = marked.parse(markdown)
    return typeof rawHtml === 'string'
      ? DOMPurify.sanitize(rawHtml, {
          ADD_TAGS: ['iframe'],
          ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
        })
      : ''
  }

  return (
    <div
      className={cn(
        styles.revision,
        !release.body && styles.noContent,
        styles[`ring-${release.ring}`]
      )}
    >
      <time
        dateTime={release.releaseDate}
        className={cn(styles.release, styles[`ring-${release.ring}`])}
      >
        {formattedDate}
      </time>
      <div className={styles.content}>
        <RingBadge className={styles.ring} ring={release.ring} size="large" />
        {release.body && (
          <div
            className={markdownStyles.prose}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Markdown is sanitized with DOMPurify
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(release.body)
            }}
          />
        )}
      </div>
    </div>
  )
}
